<template>
    <Modal
        :model-value="visible"
        cancel-text="Cancel"
        ok-text="Update languages"
        data-testid="languages-modal"
        @onOk="onOk"
        @onVisibleChange="onVisibleChange"
    >
        <template #header>
            <h3>Language</h3>
        </template>

        <div class="language-modal-wrapper">
            <div>
                <Tag>{{ campaignDefaultLanguage?.label }}</Tag>
                <TagList class="tag-list" :tags="campaignLanguagesForTags" @tagClose="onRemoveLanguage" />
            </div>

            <Input
                prefix="ios-search"
                placeholder="Start typing to search your language"
                data-testid="languages-modal__search-input"
                :clearable="true"
                :model-value="remoteQuery"
                @onChange="remoteFilter"
            />

            <div class="language-modal-body">
                <TableCheckboxGroup
                    v-model="campaignLanguagesForTable"
                    class="table-checkbox"
                    data-testid="languages-modal__checkobox-list"
                    :columns="tableColumns"
                    :data="tableData"
                    :disable-row-click="true"
                    height="300"
                    disable-select-all
                />
            </div>

            <Alert show-icon class="aid">
                <template #icon>
                    <icon type="md-information-circle" size="40" />
                </template>

                Your selected languages will be added to this campaign. Once saved, they will show in the "language"
                dropdown of your Assemble.
            </Alert>
        </div>
    </Modal>
</template>

<script lang="ts">
import TableCheckboxGroup from "@/components/TableCheckboxGroup.vue";
import TagList from "@/components/Campaign/TagList.vue";

import { campaignDefaultLanguage } from "@/store/modules/campaign";
import { languagesList, type Language, type LanguageCode, type LanguageLabel } from "@/utils/languages";

export default {
    name: "CampaignLanguagesModal",
    components: {
        TagList,
        TableCheckboxGroup
    },

    props: {
        visible: Boolean
    },

    emits: ["update", "close"],

    data() {
        return {
            addedLanguages: [] as LanguageCode[],
            remoteQuery: "",
            existingLanguages: [] as LanguageCode[],
            options: languagesList,
            tableColumns: [
                {
                    title: "Languages",
                    key: "label"
                },
                {
                    title: "Code",
                    key: "value",
                    width: 85
                }
            ]
        };
    },

    computed: {
        campaignDefaultLanguage() {
            return languagesList.find(opt => opt.value === campaignDefaultLanguage);
        },

        campaignLanguagesForTags() {
            const languagesLabels = this.selectedLanguages
                .filter(lang => lang !== campaignDefaultLanguage)
                .map(lang => languagesList?.find(opt => opt.value === lang)?.label);

            return languagesLabels as string[];
        },

        campaignLanguagesForTable: {
            get() {
                return this.addedLanguages.map(lang => languagesList.filter(language => language.value === lang).pop());
            },

            set(opts: Language[]) {
                opts.forEach(opt => this.addedLanguages.push(opt.value));
                this.remoteQuery = "";
            }
        },

        selectedLanguages() {
            return [...this.existingLanguages, ...this.addedLanguages];
        },

        tableData() {
            return this.options.filter(option => {
                return (
                    option.label.toLowerCase().indexOf(this.remoteQuery) > -1 &&
                    !this.selectedLanguages.includes(option.value)
                );
            });
        },

        storeLanguages() {
            return this.$store.state.campaign.languages;
        }
    },

    watch: {
        storeLanguages: {
            immediate: true,
            handler(val) {
                this.existingLanguages = val;
            }
        }
    },

    methods: {
        onOk() {
            this.$emit("update", this.selectedLanguages);
        },

        onVisibleChange(visible: boolean) {
            if (!visible) {
                this.addedLanguages = [];
                this.remoteQuery = "";
                this.$emit("close");
            }
        },

        onRemoveLanguage(langLabel: LanguageLabel) {
            const toRemove = languagesList.find(opt => opt.label === langLabel) as Language;

            this.addedLanguages = this.addedLanguages.filter(lang => lang !== toRemove.value);

            if (this.existingLanguages.includes(toRemove.value)) {
                this.$Modal.confirm({
                    title: `Remove language: ${toRemove.label}`,
                    content:
                        "Removing this language from this campaign will delete all translations.<br><br> This is permanent and can not be undone.",
                    onOk: () => {
                        this.existingLanguages = this.existingLanguages.filter(lang => lang !== toRemove.value);
                    }
                });
            }
        },

        remoteFilter(ev: Event) {
            this.remoteQuery = (ev.target as HTMLInputElement).value.toLowerCase();
        }
    }
};
</script>

<style>
.language-modal-wrapper .ivu-table-header .ivu-table-cell-with-selection {
    display: none !important;
}
</style>

<style lang="scss" scoped>
@use "@style/variables" as *;

.language-modal-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.language-modal-body {
    margin-top: 20px;
}

.aid {
    margin-top: 7px;
    background-color: $grey1;
    border: none;

    &.ivu-alert.ivu-alert-with-icon {
        padding: 19px 12px 22px 82px;
    }

    .ivu-alert-icon {
        padding: 15px 23px 0 11px;
    }
}
</style>
